import React, { useEffect, useState } from 'react';
import { leaveAnalyticsAPI, companiesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { BarChart3, Download, Loader2, TrendingDown } from 'lucide-react';

const LeaveAnalytics = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [company, setCompany] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [utilization, setUtilization] = useState(null);
  const [absenteeism, setAbsenteeism] = useState(null);
  const [lop, setLop] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = () => {
    const p = { year };
    if (company !== 'all') p.company_id = company;
    return p;
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const p = params();
      const [u, a, l, f, c] = await Promise.all([
        leaveAnalyticsAPI.utilization(p),
        leaveAnalyticsAPI.absenteeism({ ...p, days: 30 }),
        leaveAnalyticsAPI.lopTrend(p),
        leaveAnalyticsAPI.forecast({ ...p, days: 30 }),
        companies.length === 0 ? companiesAPI.getAll() : Promise.resolve({ data: companies }),
      ]);
      setUtilization(u.data); setAbsenteeism(a.data); setLop(l.data); setForecast(f.data);
      setCompanies(c.data || companies);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [year, company]);

  const exportCsv = async () => {
    const token = localStorage.getItem('token');
    const url = new URL(leaveAnalyticsAPI.exportUrl());
    url.searchParams.set('year', year);
    if (company !== 'all') url.searchParams.set('company_id', company);
    const r = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error('Export failed'); return; }
    const blob = await r.blob();
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `leave_export_${year}.csv`;
    link.click();
  };

  const barMax = Math.max(1, ...((lop?.series || []).map((s) => s.lop_days || 0)));

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart3 size={22} />Leave Analytics</h1>
          <p className="text-muted-foreground text-sm">Utilization, absenteeism, LOP trend and 30-day forecast.</p>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="la-export"><Download size={14} className="mr-1" />Export CSV</Button>
      </div>

      <div className="flex gap-2 items-end flex-wrap">
        <div><Label>Year</Label><Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-28" data-testid="la-year" /></div>
        <div><Label>Company</Label>
          <Select value={company} onValueChange={setCompany}>
            <SelectTrigger className="w-52" data-testid="la-company"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All companies</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Total leave days</div><div className="text-2xl font-bold" data-testid="la-total-days">{utilization?.total_days ?? 0}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Active employees</div><div className="text-2xl font-bold" data-testid="la-active">{absenteeism?.active_employees ?? 0}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Absenteeism (30d)</div><div className="text-2xl font-bold" data-testid="la-absenteeism">{absenteeism?.absenteeism_rate_pct ?? 0}%</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Forecast (next 30d)</div><div className="text-2xl font-bold" data-testid="la-forecast">{forecast?.count ?? 0}</div></CardContent></Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">By leave type</CardTitle></CardHeader>
              <CardContent className="space-y-1" data-testid="la-by-type">
                {(utilization?.by_leave_type || []).length === 0
                  ? <div className="text-sm text-muted-foreground">No approved leaves in range.</div>
                  : utilization.by_leave_type.slice(0, 12).map((r) => (
                    <div key={r.leave_type} className="flex items-center gap-2 text-sm">
                      <span className="w-28 truncate capitalize">{r.leave_type.replace('_', ' ')}</span>
                      <div className="flex-1 bg-muted/40 rounded h-2 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${Math.round((r.days / (utilization.total_days || 1)) * 100)}%` }} /></div>
                      <span className="w-12 text-right text-xs font-mono">{r.days}</span>
                    </div>
                  ))
                }
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">By department</CardTitle></CardHeader>
              <CardContent className="space-y-1" data-testid="la-by-dept">
                {(utilization?.by_department || []).length === 0
                  ? <div className="text-sm text-muted-foreground">No department data.</div>
                  : utilization.by_department.slice(0, 12).map((r) => (
                    <div key={r.department} className="flex items-center gap-2 text-sm">
                      <span className="w-28 truncate">{r.department}</span>
                      <div className="flex-1 bg-muted/40 rounded h-2 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${Math.round((r.days / (utilization.total_days || 1)) * 100)}%` }} /></div>
                      <span className="w-12 text-right text-xs font-mono">{r.days}</span>
                    </div>
                  ))
                }
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingDown size={16} />LOP trend ({lop?.year ?? year})</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-12 gap-1 items-end h-32" data-testid="la-lop-trend">
                {(lop?.series || []).map((s) => (
                  <div key={s.month} className="flex flex-col items-center justify-end gap-1">
                    <div className="w-full bg-primary/70 rounded-t" style={{ height: `${(s.lop_days / barMax) * 100}%`, minHeight: s.lop_days > 0 ? 4 : 0 }} title={`${s.month}: ${s.lop_days} days`} />
                    <span className="text-[10px] text-muted-foreground">{s.month.slice(-2)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default LeaveAnalytics;
